import { Component, EventEmitter, OnInit, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProcessCategoryService } from '../../services/process-category.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { BottomSheetComponent } from 'app/core/components/bottom-sheet/bottom-sheet.component';
import Swal from 'sweetalert2';
import { PermissionService } from '@services/core/permission.service';

@Component({
  selector: 'app-process-category-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ListCardComponent, MobileFooterComponent, BottomSheetComponent],
  templateUrl: './process-category-list.component.html',
  styleUrl: './process-category-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ProcessCategoryListComponent implements OnInit {
  @Output() selectEvent = new EventEmitter<any>();

  categories = signal<any[]>([]);
  filteredCategories: any[] = [];
  selectedUuid = signal<string | null>(null);
  isLoading = false;
  rows = Array.from({ length: 5 });

  searchText = '';
  mobileFiltersOpen = false;
  activeFilter: boolean | null = null;
  orderColumn = 'name';
  orderDirection: 'asc' | 'desc' = 'asc';

  public appSettings = inject(AppSettings);
  private categoryService = inject(ProcessCategoryService);
  readonly perm = inject(PermissionService);

  ngOnInit(): void {
    this.loadCategories();
  }

  loadCategories(): void {
    this.isLoading = true;
    this.categoryService.get(false).subscribe({
      next: (data) => {
        this.categories.set(data ?? []);
        this.applyFilters();
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  applyFilters(): void {
    const list = this.categories();
    const query = this.searchText.trim().toLowerCase();
    this.filteredCategories = list.filter((c) => {
      const matchActive = this.activeFilter === null || !!c.isActive === this.activeFilter;
      const matchText = !query
        || (c.name && c.name.toLowerCase().includes(query))
        || (c.slug && c.slug.toLowerCase().includes(query))
        || (c.abbr && c.abbr.toLowerCase().includes(query));
      return matchActive && matchText;
    });
    this.sortData();
  }

  orderCol(column: string): void {
    if (this.orderColumn === column) {
      this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.orderColumn = column;
      this.orderDirection = 'asc';
    }
    this.sortData();
  }

  private sortData(): void {
    const col = this.orderColumn;
    const factor = this.orderDirection === 'asc' ? 1 : -1;
    this.filteredCategories.sort((a, b) => {
      const valA = a[col] ?? '';
      const valB = b[col] ?? '';
      return valA > valB ? factor : (valA < valB ? -factor : 0);
    });
  }

  selectCategory(category: any): void {
    this.selectedUuid.set(category?.uuid ?? null);
    this.selectEvent.emit(category);
  }

  setActiveFilter(value: boolean | null): void {
    this.activeFilter = value;
    this.applyFilters();
  }

  deleteCategory(category: any): void {
    Swal.fire({
      title: `¿Eliminar categoría "${category.name}"?`,
      text: 'No se puede eliminar si hay procesos usándola.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((res) => {
      if (res.isConfirmed) this.executeDelete(category.uuid);
    });
  }

  private executeDelete(uuid: string): void {
    this.categoryService.delete(uuid).subscribe({
      next: () => {
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Categoría eliminada', timer: 3000, showConfirmButton: false });
        this.loadCategories();
      },
      error: (err) => {
        Swal.fire('Error', err?.error?.message ?? err?.error?.error ?? 'No se pudo eliminar', 'error');
      }
    });
  }
}
